import { Button } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormRecipient } from '@/api';
import { AddressInput } from '@/components/FormCreate/AddressInput';
import { addRecipientByEmail, useAddressPool } from '@/components/FormCreate/createForm';
import { LedgerRow } from '@/components/Ui/LedgerRow';
import { Section } from '@/components/Ui/Section';
import { Tag } from '@/components/Ui/Tag';
import { useUndoToast } from '@/components/Ui/Toast';
import { relativeTime } from '@/lib/relative-time';
import { errorCode, errorMessage } from '@/pages/Account/format';
import { useRustyState } from '@/state';
import classes from './FormOverview.module.css';
import type { SectionProps } from './types';

const QUIET = { onSuccess: false } as const;

export function RecipientsSection({ form, data }: Pick<SectionProps, 'form' | 'data'>) {
  const { t, i18n } = useTranslation();
  const notify = useUndoToast();
  const { pool } = useAddressPool();
  const [resendError, setResendError] = useState<{ id: string; text: string } | null>(null);
  const recipients = data.recipients ?? [];
  const verified = recipients.filter((r) => r.is_verified);
  const taken = recipients.map((r) => r.email.toLowerCase());
  const suggestions = pool
    .filter((e) => !taken.includes(e.email.toLowerCase()))
    .map((e) => e.email);

  const refresh = async () => {
    const state = useRustyState.getState();
    await Promise.all([data.reloadRecipients(), data.reloadStatus()]);
    void state.refreshProblemsCount();
    void state.getAndSetVerifiedEmails().catch(() => undefined);
  };

  const attach = (id: string) =>
    useRustyState
      .getState()
      .api.newFormRecipient({ form_id: form.id, verified_email_id: id }, QUIET);

  const detach = (id: string) => useRustyState.getState().api.deleteFormRecipient(form.id, id);

  const add = async (email: string) => {
    if (taken.includes(email.toLowerCase())) return;
    const known = pool.find((e) => e.email.toLowerCase() === email.toLowerCase());
    let id: string;
    if (known) {
      await attach(known.id);
      id = known.id;
    } else {
      const row = await addRecipientByEmail(form.id, email, t);
      id = row.id;
    }
    await refresh();
    const message = !known
      ? 'form.recipients.addedNew'
      : known.is_verified
        ? 'form.recipients.added'
        : 'form.recipients.addedPending';
    notify(t(message, { email }), async () => {
      await detach(id);
      await refresh();
    });
  };

  const remove = async (r: FormRecipient) => {
    try {
      await detach(r.id);
    } catch {
      // the API layer reports why, e.g. the last recipient of a form with auto-reply on
      return;
    }
    await refresh();
    notify(t('form.recipients.removed', { email: r.email }), async () => {
      await attach(r.id);
      await refresh();
    });
  };

  const resend = async (r: FormRecipient) => {
    setResendError(null);
    try {
      await useRustyState
        .getState()
        .api.verifyVerifiedEmail(r.id, { onSuccess: false, onError: false });
    } catch (err) {
      setResendError({
        id: r.id,
        text:
          errorCode(err) === 'resend_limit'
            ? t('addresses.add.resendLimit')
            : (errorMessage(err) ?? t('addresses.resendFailed')),
      });
      return;
    }
    notify(t('form.recipients.resent', { email: r.email }));
    void data.reloadRecipients();
  };

  const subtitle =
    verified.length === 1
      ? t('form.recipients.notifies', { email: verified[0].email })
      : verified.length > 1
        ? t('form.recipients.each')
        : undefined;

  return (
    <Section id="recipients" title={t('form.recipients.title')} subtitle={subtitle}>
      {data.recipients !== null && recipients.length === 0 && (
        <LedgerRow
          label={t('form.recipients.title')}
          value={t('form.recipients.none')}
          risk={t('form.recipients.noneRisk')}
        />
      )}
      {recipients.map((r) => (
        <LedgerRow
          key={r.id}
          wideLabel
          label={<span className={classes.mono}>{r.email}</span>}
          value={
            <span className={classes.tags}>
              {r.is_verified ? (
                <Tag variant="ok">{t('ui.verified')}</Tag>
              ) : (
                <Tag variant="pending">{t('ui.pendingVerification')}</Tag>
              )}
              {r.is_account_email && <Tag>{t('ui.accountEmail')}</Tag>}
            </span>
          }
          help={
            r.is_verified
              ? undefined
              : t('form.recipients.pendingHelp', {
                  when: relativeTime(r.last_requested_at, i18n.language),
                })
          }
          risk={resendError?.id === r.id ? resendError.text : undefined}
          action={
            <>
              {!r.is_verified && (
                <Button variant="subtle" size="xs" onClick={() => void resend(r)}>
                  {t('ui.resend')}
                </Button>
              )}
              <Button
                variant="subtle"
                size="xs"
                onClick={() => void remove(r)}
                aria-label={t('form.recipients.removeLabel', { email: r.email })}
              >
                {t('ui.remove')}
              </Button>
            </>
          }
        />
      ))}
      <div className={classes.block}>
        <AddressInput
          label={t('form.recipients.addLabel')}
          suggestions={suggestions}
          onAdd={add}
          placeholder={t('create.recipientPlaceholder')}
          help={t('form.recipients.addHelp')}
          withButton
        />
      </div>
    </Section>
  );
}
